import { useMemo, useState } from "react";
import { CheckCircleIcon, EyeIcon, EyeSlashIcon, KeyIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { extractApiError } from "@/lib/api-error";
import { activateAdmin } from "../api/authApi";

const getApiErrorCode = (error: unknown) => {
    const code = (error as { response?: { data?: { errorCode?: unknown } } })?.response?.data?.errorCode;
    return typeof code === "string" ? code : null;
};

const passwordRules = [
    { label: "8–128 characters", test: (value: string) => value.length >= 8 && value.length <= 128 },
    { label: "One uppercase letter", test: (value: string) => /[A-Z]/.test(value) },
    { label: "One lowercase letter", test: (value: string) => /[a-z]/.test(value) },
    { label: "One number", test: (value: string) => /\d/.test(value) },
    { label: "One special character", test: (value: string) => /[^A-Za-z0-9]/.test(value) },
];

export const ActivateAccountRoute = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const passwordChecks = useMemo(
        () => passwordRules.map((rule) => ({ ...rule, valid: rule.test(password) })),
        [password]
    );

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);

        if (!email.trim()) return setError("Enter the email address from your invitation.");
        if (!/^\d{6}$/.test(otp)) return setError("Enter the six-digit OTP from your invitation email.");
        if (!passwordChecks.every((rule) => rule.valid)) return setError("Choose a password that meets all listed requirements.");
        if (password !== confirmPassword) return setError("Passwords do not match.");

        setIsLoading(true);
        try {
            await activateAdmin({ email: email.trim(), otp, newPassword: password });
            navigate("/auth/login", {
                state: { message: "Account activated successfully. You can now sign in." },
            });
        } catch (activationError) {
            setError(
                getApiErrorCode(activationError) === "INVALID_ADMIN_INVITATION"
                    ? "The invitation details are invalid, expired, or locked. Check your invitation and try again."
                    : extractApiError(activationError)
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-8">
            <header className="space-y-2">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300">
                    <ShieldCheckIcon className="h-6 w-6" />
                </div>
                <h1 className="pt-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Activate your account</h1>
                <p className="text-sm leading-6 text-slate-500 dark:text-slate-300">Use the six-digit code from your invitation email to choose your password.</p>
            </header>

            <div className="border border-slate-200 bg-white/80 p-8 shadow-xl shadow-slate-900/5 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/80">
                <form className="space-y-5" onSubmit={handleSubmit} noValidate>
                    <div className="space-y-1.5">
                        <label htmlFor="activation-email" className="block text-sm font-medium text-slate-700 dark:text-slate-200">Invitation email</label>
                        <input id="activation-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@ridetofive.com" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100" />
                    </div>

                    <div className="space-y-1.5">
                        <label htmlFor="activation-otp" className="block text-sm font-medium text-slate-700 dark:text-slate-200">Six-digit invitation code</label>
                        <input id="activation-otp" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-center font-mono text-2xl tracking-[0.35em] text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100" />
                    </div>

                    <div className="space-y-1.5">
                        <label htmlFor="activation-password" className="block text-sm font-medium text-slate-700 dark:text-slate-200">New password</label>
                        <div className="relative">
                            <KeyIcon className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                            <input id="activation-password" type={showPassword ? "text" : "password"} autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Create your password" className="w-full rounded-xl border border-slate-200 bg-white px-10 py-3 pr-12 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100" />
                            <button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400" aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}</button>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <label htmlFor="activation-confirm-password" className="block text-sm font-medium text-slate-700 dark:text-slate-200">Confirm password</label>
                        <div className="relative">
                            <KeyIcon className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                            <input id="activation-confirm-password" type={showConfirmPassword ? "text" : "password"} autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="Re-enter your password" className="w-full rounded-xl border border-slate-200 bg-white px-10 py-3 pr-12 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100" />
                            <button type="button" onClick={() => setShowConfirmPassword((value) => !value)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400" aria-label={showConfirmPassword ? "Hide password" : "Show password"}>{showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}</button>
                        </div>
                    </div>

                    <div className="rounded-xl bg-slate-50 p-4 dark:bg-slate-950/70">
                        <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">Password requirements</p>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                            {passwordChecks.map(({ label, valid }) => <div key={label} className={`flex items-center gap-2 text-xs ${valid ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400"}`}><span className={`flex h-4 w-4 items-center justify-center rounded-full ${valid ? "bg-emerald-500 text-white" : "bg-slate-200 dark:bg-slate-800"}`}>{valid ? <CheckCircleIcon className="h-3 w-3" /> : null}</span>{label}</div>)}
                        </div>
                    </div>

                    {error ? <p className="text-sm font-medium text-rose-500">{error}</p> : null}
                    <Button type="submit" className="w-full rounded-full" disabled={isLoading}>{isLoading ? "Activating..." : "Activate account"}</Button>
                </form>
            </div>

            <div className="text-center text-sm"><Link to="/auth/login" className="font-medium text-primary-600 transition hover:text-primary-500">← Back to login</Link></div>
        </div>
    );
};
